import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { unlockRequestPin } from "actions/Units";
import ButtonContained from "components/ButtonContained";
import PopoutDropdown from "components/PopoutDropdown";
import TextInput from "components/TextInput";

const EmergencyUnlock = ({ lock, unit, handleEmergencyUnlock }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const authUser = useSelector((state) => state.auth.authUser);
  const [type, setType] = useState("");
  const [selectedReason, setSelectedReason] = useState(null);
  const [reasonText, setReasonText] = useState("");
  const [pinSent, setPinSent] = useState(false);
  const [pinCode, setPinCode] = useState("");

  // an extra check here since they do not want override on repos or overlocks
  const hasOverride = authUser.permissions.includes("access_override_unlock") && unit.rentalState !== 'repo' && unit.rentalState !== 'overlock';
  const hasEmergency = authUser.permissions.includes("access_emergency_unlock");

  useEffect(() => {
    if (!hasOverride && hasEmergency) {
      setType("emergency");
    } else if (hasOverride && !hasEmergency) {
      setType("pin");
      dispatch(unlockRequestPin(unit.User.uuid));
      setPinSent(true);
    }
  }, [hasOverride, hasEmergency]);

  const options = [
    {
      name: intl.formatMessage({ id: "form.unlockOccupiedEmergencyReasonFire" }),
      value: "fire"
    },
    {
      name: intl.formatMessage({ id: "form.unlockOccupiedEmergencyReasonFlood" }),
      value: "flood"
    },
    {
      name: intl.formatMessage({ id: "form.unlockOccupiedEmergencyReasonTenantPhone" }),
      value: "tenantPhone"
    },
    {
      name: intl.formatMessage({ id: "form.select.other" }),
      value: "other"
    }
  ];

  return (
    <div className="space-y-4 px-10 min-h-[40vh] max-h-[60vh] flex flex-col overflow-auto custom-scrollbar mr-1">
      <div className="h-12 flex justify-center gap-2">
        {hasOverride && <ButtonContained
          id={`Emergency-Unlock-${lock.id}-Request-Pin-Select`}
          dataCy={`Emergency-Unlock-${lock.id}-Request-Pin-Select`}
          text={intl.formatMessage({ id: "comp.integration.request" })}
          type={type === "pin" ? "default" : "inactive"}
          onClick={() => {
            type !== "pin" && setType("pin");
            if (!pinSent) {
              dispatch(unlockRequestPin(unit.User.uuid));
              setPinSent(true);
            }
          }}
        />}
        {hasEmergency && <ButtonContained
          id={`Emergency-Unlock-${lock.id}-Emergency-Select`}
          dataCy={`Emergency-Unlock-${lock.id}-Emergency-Select`}
          text={intl.formatMessage({ id: "comp.button.emergency" })}
          type={type === "emergency" ? "default" : "inactive"}
          onClick={() => type !== "emergency" && setType("emergency")}
        />}
      </div>

      {type === "pin" && (
        <div className="rounded-2xl bg-white p-4 mx-auto relative flex flex-col items-center">
          <p className="tw-paragraph-reg mb-4 text-center">
            {intl.formatMessage({ id: "form.unlockOccupiedDesc" })}
          </p>
          {(lock.bleHwVersion === "1A" || lock.bleHwVersion === "2A") && <p className="tw-paragraph-reg mb-4 text-center">
            {intl.formatMessage({ id: "form.unlockOccupiedNote" })}
          </p>}
          <TextInput
            id={`Emergency-Unlock-${lock.id}-Pin-Code-Input`}
            dataCy={`Emergency-Unlock-${lock.id}-Pin-Code-Input`}
            placeholder={`${intl.formatMessage({ id: "comp.table.header.pin" })} ${intl.formatMessage({ id: "comp.table.header.code" })}`}
            onChange={(e) => setPinCode(e.target.value)}
            value={pinCode}
            validateInput={(val) => /^\d*$/.test(val)} // only digits
          />
        </div>
      )}
      {type === "emergency" && (
        <div className="rounded-2xl bg-white p-4 mx-auto relative flex flex-col gap-2 items-center">
          <p className="tw-paragraph-reg mb-4 text-center">
            {intl.formatMessage({ id: "form.unlockOccupiedEmergencyDesc" })}
          </p>
          {(lock.bleHwVersion === "1A" || lock.bleHwVersion === "2A") && <p className="tw-paragraph-reg mb-4 text-center">
            {intl.formatMessage({ id: "form.unlockOccupiedNote" })}
          </p>}
          <PopoutDropdown
            options={options}
            id={`Emergency-Unlock-${lock.id}-Reason-Select`}
            dataCy={`Emergency-Unlock-${lock.id}-Reason-Select`}
            label={
              selectedReason === "" || selectedReason === null
                ? intl.formatMessage({ id: `form.unlockOccupiedEmergencyReasonSelect` })
                : options.find((opt) => opt.value === selectedReason)?.name || selectedReason
            }
            align="left"
            onOptionSelect={(option) => setSelectedReason(option)}
          />
          {selectedReason === "other" && (
            <TextInput
              id={`Emergency-Unlock-${lock.id}-Reason-Other-Input`}
              dataCy={`Emergency-Unlock-${lock.id}-Reason-Other-Input`}
              placeholder={`${intl.formatMessage({ id: "form.unlockOccupiedEmergencyReasonOther" })}`}
              onChange={(e) => setReasonText(e.target.value)}
              value={reasonText}
            />
          )}
        </div>
      )}
      {(type === "pin" || type === "emergency") && <div className="h-12 flex justify-center gap-2">
        <ButtonContained
          id={`Emergency-Unlock-${lock.id}-Unlock-Button`}
          dataCy={`Emergency-Unlock-${lock.id}-Unlock-Button`}
          text={intl.formatMessage({ id: `form.unlock` })}
          type="default"
          disabled={(type === "pin" && pinCode === "") || (type === "emergency" && (selectedReason === null || (selectedReason === "other" && reasonText === "")))}
          onClick={() => {
            let reason = reasonText;

            if (selectedReason && selectedReason !== "other") {
              reason = options.find((opt) => opt.value === selectedReason)?.name || selectedReason;
            }

            handleEmergencyUnlock(lock, unit, type, selectedReason, reason, pinCode)
          }}
        />
      </div>}
    </div>
  );
};

export default EmergencyUnlock;
